import React from 'react';
import PropTypes from 'prop-types';
import { useSidebar } from '../contexts/SidebarContext.js';

const PAGE_SIZE = 30;

// ⚡ Bolt: Memoized HashtagItem to prevent O(N) re-renders in Sidebar
const HashtagItem = React.memo(({ tagObj, state, onFilterChange }) => {
  const tag = '#' + tagObj.tag;

  const handleYesClick = React.useCallback(() => {
    onFilterChange(tag, 'yes');
  }, [tag, onFilterChange]);

  const handleNeutralClick = React.useCallback(() => {
    onFilterChange(tag, 'neutral');
  }, [tag, onFilterChange]);

  const handleNoClick = React.useCallback(() => {
    onFilterChange(tag, 'no');
  }, [tag, onFilterChange]);

  return (
    <div className={`hashtag-item hashtag-item--${state}`}>
      <div className="hashtag-item__label">
        <span className="hashtag-item__name">{tag}</span>
        <span className="hashtag-item__count">{tagObj.video_count}</span>
      </div>
      <div className="hashtag-item__toggle" role="group" aria-label={`Filter ${tag}`}>
        <button
          type="button"
          className={`toggle-button${state === 'yes' ? ' is-active' : ''}`}
          aria-pressed={state === 'yes'}
          onClick={handleYesClick}
        >
          Yes
        </button>
        <button
          type="button"
          className={`toggle-button${state === 'neutral' ? ' is-active' : ''}`}
          aria-pressed={state === 'neutral'}
          onClick={handleNeutralClick}
        >
          ?
        </button>
        <button
          type="button"
          className={`toggle-button${state === 'no' ? ' is-active' : ''}`}
          aria-pressed={state === 'no'}
          onClick={handleNoClick}
        >
          No
        </button>
      </div>
    </div>
  );
});

HashtagItem.displayName = 'HashtagItem';
HashtagItem.propTypes = {
  tagObj: PropTypes.shape({
    tag: PropTypes.string.isRequired,
    video_count: PropTypes.number.isRequired,
  }).isRequired,
  state: PropTypes.oneOf(['yes', 'no', 'neutral']).isRequired,
  onFilterChange: PropTypes.func.isRequired,
};

const Sidebar = ({ filters, onFilterChange, onReset }) => {
  const { isCollapsed } = useSidebar();
  const [tags, setTags]       = React.useState([]);
  const [hasMore, setHasMore] = React.useState(true);
  const [loading, setLoading] = React.useState(false);
  const sentinelRef           = React.useRef(null);
  const inflightRef           = React.useRef(false);
  // Keep offset in a ref so the IntersectionObserver closure always sees it fresh
  const offsetRef             = React.useRef(0);

  const fetchPage = React.useCallback(async (fetchOffset) => {
    if (inflightRef.current) return;
    inflightRef.current = true;
    setLoading(true);
    try {
      const res = await fetch(`api/tags.php?offset=${fetchOffset}&limit=${PAGE_SIZE}`);
      if (!res.ok) throw new Error(`tags.php ${res.status}`);
      const data = await res.json();
      setTags((prev) => fetchOffset === 0 ? data.tags : [...prev, ...data.tags]);
      const nextOffset = fetchOffset + data.tags.length;
      offsetRef.current = nextOffset;
      setHasMore(data.has_more);
    } catch (err) {
      console.error('Sidebar tag fetch failed:', err);
    } finally {
      setLoading(false);
      inflightRef.current = false;
    }
  }, []);

  // Initial load
  React.useEffect(() => {
    fetchPage(0);
  }, [fetchPage]);

  // Infinite scroll — watch the sentinel div at the bottom of the list
  React.useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          fetchPage(offsetRef.current);
        }
      },
      { rootMargin: '120px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [fetchPage, hasMore]);

  return (
    <aside
      id="hashtag-sidebar"
      className={`hashtag-sidebar${isCollapsed ? ' hashtag-sidebar--collapsed' : ''}`}
    >
      <div className="hashtag-sidebar__header">
        <h2>Filters</h2>
        <button
          type="button"
          className="hashtag-sidebar__reset"
          onClick={onReset}
        >
          Reset
        </button>
      </div>

      <div className="hashtag-sidebar__list">
        {/* ⚡ Bolt: Extracted mapping to memoized child components to prevent O(N) re-renders */}
        {tags.filter((tagObj) => typeof tagObj.tag === 'string' && tagObj.tag.toLowerCase() !== 'asmr').map((tagObj) => {
          const tag   = '#' + tagObj.tag;
          const state = filters[tag] || 'neutral';
          return (
            <HashtagItem
              key={tagObj.tag_id}
              tagObj={tagObj}
              state={state}
              onFilterChange={onFilterChange}
            />
          );
        })}

        {/* Sentinel — IntersectionObserver fires here to load the next page */}
        {hasMore && <div ref={sentinelRef} className="hashtag-sidebar__sentinel" />}

        {loading && (
          <div className="hashtag-sidebar__loading" aria-live="polite">
            Loading…
          </div>
        )}

        {!hasMore && !loading && tags.length > 0 && (
          <div className="hashtag-sidebar__end">— all tags loaded —</div>
        )}

        {!loading && tags.length === 0 && (
          <div className="hashtag-sidebar__empty">No tags available yet.</div>
        )}
      </div>
    </aside>
  );
};

Sidebar.propTypes = {
  filters:        PropTypes.objectOf(PropTypes.oneOf(['yes', 'no', 'neutral'])).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onReset:        PropTypes.func.isRequired,
};

export default React.memo(Sidebar);
